import { useEffect, useState } from "react";
import { Box, Stack } from "@mui/material";

import type { Workout } from "../../api/workout-service";

import WorkoutUsersBlock from "./WorkoutUsersBlock";
import { WorkoutTopBar } from "./WorkoutTopBar";
import { QuestionCard } from "./QuestionCard";
import { QUIZ_SURFACE } from "./quiz-theme";

export type WorkoutProcessBlockProps = {
    workout: Workout | null;
    show?: boolean;
    answerSubmitAction: (questionId: string, answerIndex: number, onSuccess: () => void, onError: () => void) => void;
    finishAction: () => void;
    failedAction: () => void;
};

export default function WorkoutProcessBlock(
    { workout, show, answerSubmitAction, finishAction, failedAction }: WorkoutProcessBlockProps) {

    const [ submitted, setSubmitted ] = useState(false);
    const [ questionIndex, setQuestionIndex ] = useState(0);
    const [ answer, setAnswer ] = useState(-1);
    const [ lives, setLives ] = useState<number | null>(null);

    useEffect(() => {
        if (!show || !workout) return;

        setLives(workout.currentUser?.lives ?? null);

        if (answer !== -1 || submitted) return;

        const previousAnswer = workout.questions[questionIndex + 1]
            ?.answers
            ?.find(answer => answer.user === workout.currentUser?.id)
            ?.index;

        setAnswer(previousAnswer ?? -1);
        setSubmitted(previousAnswer !== undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ workout ]);

    if (!show || !workout) return <></>

    // Bound to a const so the closures below keep the non-null narrowing
    const activeWorkout = workout;

    const questionAmount = activeWorkout.questions.length;
    const currentQuestion = activeWorkout.questions[questionIndex];

    function skipAction() {
        answerSubmitAction(currentQuestion.id, answer, () => {
            setSubmitted(true);
        }, () => {
        });
    }

    function nextAction() {
        if (activeWorkout.currentUser?.lives === 0) {
            failedAction();
            return;
        }

        if (questionIndex + 1 >= questionAmount) {
            finishAction();
            return;
        }

        const previousAnswer = activeWorkout.questions[questionIndex + 1]
            ?.answers
            ?.find(answer => answer.user === activeWorkout.currentUser?.id)
            ?.index;

        setAnswer(previousAnswer ?? -1);
        setSubmitted(previousAnswer !== undefined);
        setQuestionIndex(questionIndex + 1);
    }

    function submitAction() {
        answerSubmitAction(currentQuestion.id, answer, () => {
            setSubmitted(true);
        }, () => {
        });
    }

    return <>
        <WorkoutTopBar workout={ activeWorkout } questionIndex={ questionIndex }/>

        <Stack component="main" spacing={ 3 } sx={ { flexGrow: 1, px: { xs: 2, sm: 4 }, pt: 4, pb: 4 } }>
            <QuestionCard workoutId={ activeWorkout.id } question={ currentQuestion } answer={ answer } submitted={ submitted }
                          countsLives={ lives !== null } onSelect={ setAnswer } onSubmit={ submitAction } onSkip={ skipAction } onNext={ nextAction }/>

            { activeWorkout.users.length > 1 && (
                <Box sx={ { ...QUIZ_SURFACE, width: '100%', maxWidth: 860, mx: 'auto', p: 3 } }>
                    <WorkoutUsersBlock users={ activeWorkout.users } answers={ currentQuestion.answers } show/>
                </Box>
            ) }
        </Stack>
    </>
}
